import { useState } from 'react';

import type { SimulatorChannel } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  useChannelConnectionQuery,
  useDisconnectChannelMutation,
} from '@/features/msg-channels/common/msg-channels.query';
import { cn } from '@/lib/utils';

import { isSessionId } from './channel-simulator.hooks';
import {
  useClearSimulatorEventsMutation,
  useSimulatorLinkCodeMutation,
  useSimulatorSessionQuery,
} from './channel-simulator.query';
import {
  SIMULATOR_CHANNEL_LABELS,
  SIMULATOR_CHANNEL_TYPES,
  SIMULATOR_CHANNELS,
} from './channel-simulator.types';

const SessionField = ({
  sessionId,
  onSessionIdChange,
}: {
  sessionId: string;
  onSessionIdChange: (sessionId: string) => void;
}) => {
  const [draftSessionId, setDraftSessionId] = useState(sessionId);
  const isValid = isSessionId(draftSessionId);

  return (
    <form
      className="flex flex-col gap-1 text-xs"
      onSubmit={(event) => {
        event.preventDefault();
        if (isValid) onSessionIdChange(draftSessionId);
      }}
    >
      <span className="font-semibold text-foreground-subtle">Session</span>
      <XStack className="gap-1">
        <Input
          aria-label="Session id"
          value={draftSessionId}
          onChange={(event) => setDraftSessionId(event.target.value)}
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          disabled={!isValid || draftSessionId === sessionId}
        >
          Switch
        </Button>
      </XStack>
      <span
        className={cn('text-foreground-subtle', !isValid && 'text-warning-11')}
      >
        Lowercase letters, digits and hyphens, at most 32. Each session has its
        own devices and log.
      </span>
    </form>
  );
};

export const SimulatorControls = ({
  sessionId,
  onSessionIdChange,
  channel,
  onChannelChange,
  onPrefill,
}: {
  sessionId: string;
  onSessionIdChange: (sessionId: string) => void;
  channel: SimulatorChannel;
  onChannelChange: (channel: SimulatorChannel) => void;
  onPrefill: (text: string) => void;
}) => {
  const session = useSimulatorSessionQuery(sessionId);
  const readiness = session.data?.channels[channel];
  const channelType = SIMULATOR_CHANNEL_TYPES[channel];
  const connection = useChannelConnectionQuery(channelType, {
    refetchInterval: 3000,
  });
  const disconnect = useDisconnectChannelMutation(channelType);
  const linkCode = useSimulatorLinkCodeMutation(channel);
  const clear = useClearSimulatorEventsMutation(sessionId);
  const linkedIdentifier = connection.data?.identifier ?? null;
  const isLinkedToThisSession =
    linkedIdentifier === readiness?.sender_identifier;

  return (
    <YStack className="w-64 shrink-0 gap-4">
      <YStack className="gap-1">
        <h1 className="text-lg font-semibold">Channel simulator</h1>
        <span className="text-xs text-foreground-subtle">
          Messages go through the real webhook, intake, and agent. Provider
          calls are recorded instead of sent.
        </span>
      </YStack>
      <SessionField
        key={sessionId}
        sessionId={sessionId}
        onSessionIdChange={onSessionIdChange}
      />
      <XStack className="gap-1">
        {SIMULATOR_CHANNELS.map((option) => (
          <Button
            key={option}
            size="sm"
            variant={option === channel ? 'default' : 'outline'}
            onClick={() => onChannelChange(option)}
          >
            {SIMULATOR_CHANNEL_LABELS[option]}
          </Button>
        ))}
      </XStack>
      <YStack className="gap-1 text-xs">
        <span className="font-semibold text-foreground-subtle">Device</span>
        <span className="break-all font-mono">
          {readiness?.sender_identifier}
        </span>
        {readiness && !readiness.ready && (
          <span className="text-warning-11">
            Set in backend/.env: {readiness.missing_settings.join(', ')}
          </span>
        )}
      </YStack>
      <YStack className="gap-1 text-xs">
        <span className="font-semibold text-foreground-subtle">Link state</span>
        {linkedIdentifier === null ? (
          <span>Not linked. Connect, then send the prefilled message.</span>
        ) : (
          <span className={cn(!isLinkedToThisSession && 'text-warning-11')}>
            Linked to {linkedIdentifier}
            {!isLinkedToThisSession &&
              ' (another device; connect to move the link to this session)'}
          </span>
        )}
      </YStack>
      <YStack className="gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={linkCode.isPending || !readiness?.ready}
          onClick={() =>
            linkCode.mutate(undefined, {
              onSuccess: (result) => onPrefill(result.message),
            })
          }
        >
          Connect (prefill link code)
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={disconnect.isPending || linkedIdentifier === null}
          onClick={() => disconnect.mutate()}
        >
          Disconnect
        </Button>
        <Button
          size="sm"
          variant="destructive-ghost"
          disabled={clear.isPending}
          onClick={() => clear.mutate()}
        >
          Clear log
        </Button>
      </YStack>
    </YStack>
  );
};
